import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { articlesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, BookOpen, ArrowRight, Calendar, User } from 'lucide-react';

export default function BlogPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function loadArticles() {
    async function fetchData() {
      try {
        const articlesData = await articlesApi.getAll(selectedCategory);
        setArticles(articlesData.articles || []);
      } catch (err) {
        console.error('Failed to fetch articles:', err);
      }
      try {
        const categoriesData = await articlesApi.getCategories();
        setCategories(categoriesData.categories || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, [selectedCategory]);

  function handleCategoryClick(category) {
    if (selectedCategory === category) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                AICreditPilot
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">Home</Link>
              <Link to="/blog" className="text-teal-600 font-medium">Blog</Link>
              <Link to="/#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button 
                  onClick={function() { navigate('/dashboard'); }}
                  className="btn-primary"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <React.Fragment>
                  <Button 
                    variant="ghost" 
                    onClick={function() { navigate('/login'); }}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="login-btn"
                  >
                    Log In
                  </Button>
                  <Button 
                    onClick={function() { navigate('/register'); }}
                    className="btn-teal"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <BookOpen className="h-4 w-4" />
            Credit Education
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
            Credit Knowledge Center
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Expert articles, tips, and guides to help you understand and improve your credit.
          </p>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-6 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={function() { setSelectedCategory(null); }}
              className={selectedCategory === null ? "bg-slate-900" : ""}
              data-testid="category-all"
            >
              All Articles
            </Button>
            {categories.map(function(category) {
              const isSelected = selectedCategory === category;
              return (
                <Button
                  key={category}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={function() { handleCategoryClick(category); }}
                  className={isSelected ? "bg-teal-600 hover:bg-teal-700" : ""}
                  data-testid={'category-' + category.toLowerCase().replace(/\s+/g, '-')}
                >
                  {category}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(function(i) {
                return (
                  <div key={i} className="animate-pulse">
                    <div className="h-48 bg-slate-200 rounded-xl mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-1/4 mb-2" />
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-full" />
                  </div>
                );
              })}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No articles yet</h3>
              <p className="text-slate-500">Check back soon for credit education content.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map(function(article) {
                return (
                  <Card 
                    key={article.article_id} 
                    className="border-slate-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={function() { navigate('/blog/' + article.slug); }}
                    data-testid={'article-card-' + article.slug}
                  >
                    {article.featured_image ? (
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={article.featured_image} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-teal-100 to-slate-100 flex items-center justify-center">
                        <BookOpen className="h-12 w-12 text-teal-300" />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <Badge variant="secondary" className="mb-3 bg-teal-50 text-teal-700">
                        {article.category}
                      </Badge>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors" style={{ fontFamily: 'Manrope' }}>
                        {article.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(article.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {article.author_name}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
            Ready to Improve Your Credit?
          </h2>
          <p className="text-slate-400 mb-8">
            Let AICreditPilot handle your disputes automatically while you focus on what matters.
          </p>
          <Button 
            onClick={function() { navigate('/register'); }}
            className="btn-teal h-12 px-8"
            data-testid="cta-btn"
          >
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-teal-500" />
              <span className="font-bold text-white" style={{ fontFamily: 'Manrope' }}>AICreditPilot</span>
            </div>
            <p className="text-slate-500 text-sm text-center">
              © 2026 AICreditPilot. Educational service. No credit score increases guaranteed.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
